import styled from '@emotion/styled';
import { Color } from '../../../styles/Color';
import PoiNoImage from '../../../assets/icons/no-image.svg';
import { RouterLink } from '../../../components/common/link/RouterLink';
import { ArrowForwardIcon, Css, ITheme, Responsive } from '../../../ui';
import { getAirportMapUrlForPoiId } from '../../map/domain/helpers/getAirportMapUrl';
import { AirportCodes } from '../../../types/airports';
import { getPoiImageUrl } from '../domain/utils/getPoiImageUrl';
import { getPoiCardIcon } from '../search/domain/utils/getPoiCardIcon';
import {
	HOVER_ZOOM_IMAGE_TRANSFORM,
	HOVER_ZOOM_IMAGE_TRANSITION,
} from '../../../styles/animation/hoverZoomImage';
import { IPoiItem } from '../domain/models/IPoiItem';
import { isPoiOpen } from '../search/domain/utils/isPoiOpen';

interface IPoiCardProps {
	poi: IPoiItem;
	airportCode: AirportCodes;
}

export default function PoiCard({ poi, airportCode }: IPoiCardProps) {
	const isMobileXL = Responsive.useMatchesBreakpoint('mobileXL');
	const { poiId, image, name, operationHours, terminal, nearbyLandmark } = poi;

	const icon = getPoiCardIcon(poi);
	const poiMapUrl = getAirportMapUrlForPoiId(airportCode, poiId);
	const isOpen = isPoiOpen(operationHours);
	const openStatusLabel = isOpen ? 'Open' : 'Closed';

	const renderPoiImage = () => {
		if (image) {
			const poiImage = getPoiImageUrl(image);
			return (
				<PoiImageWrap>
					<PoiImage src={poiImage} alt={name} loading="lazy" />
				</PoiImageWrap>
			);
		}
		return (
			<PoiNoImageWrap>
				<PoiNoImage aria-label="No image to display" className="no-image" />
			</PoiNoImageWrap>
		);
	};

	return (
		<li className="no-max-width">
			<StyledPoiLink href={poiMapUrl} target="_blank">
				<PoiCardContainer>
					{renderPoiImage()}

					<PoiCardContent>
						{operationHours && (
							<PoiOpenStatus isOpen={isOpen}>{openStatusLabel}</PoiOpenStatus>
						)}
						<PoiCardTitle>{name}</PoiCardTitle>
						{icon && (
							<PoiCardIcon>
								<PoiCardIconInner>{icon}</PoiCardIconInner>
							</PoiCardIcon>
						)}

						<PoiCardMeta>
							<PoiBadges>
								{terminal && <PoiCardBadge>{terminal}</PoiCardBadge>}

								{nearbyLandmark && (
									<PoiCardBadge>{nearbyLandmark}</PoiCardBadge>
								)}
							</PoiBadges>

							<PoiCardLinkContainer>
								{!isMobileXL && (
									<DetailsLabel className="button-label">Details</DetailsLabel>
								)}
								<ArrowForwardIcon aria-label="See card Details" />
							</PoiCardLinkContainer>
						</PoiCardMeta>
					</PoiCardContent>
				</PoiCardContainer>
			</StyledPoiLink>
		</li>
	);
}

const POI_MOBILE_IMAGE_WIDTH = '33%';

const StyledPoiLink = styled(RouterLink)`
	text-decoration: none;
`;

const PoiCardContainer = styled.div`
	display: flex;
	flex-direction: column;
	position: relative;
	overflow: hidden;
	border-radius: ${Css.pxToRem(8)};
	height: ${Css.pxToRem(310)};
	width: 100%;
	border: 1px solid ${Color.BorderCard};

	&:hover {
		img,
		.no-image {
			transform: ${HOVER_ZOOM_IMAGE_TRANSFORM};
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		display: flex;
		flex-direction: row;
		justify-content: space-evenly;
		height: ${Css.pxToRem(144)};
	}
`;

const PoiCardContent = styled.div`
	position: absolute;
	left: 0;
	bottom: 0;
	right: 0;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	gap: ${Css.pxToRem(10)};
	padding: ${Css.pxToRem(16)};
	padding-bottom: ${Css.pxToRem(18)};
	background-color: ${Color.Neutral2};
	margin: ${Css.pxToRem(8)};
	border-radius: 4px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		position: relative;
		margin: 0;
		border-radius: 0;
		padding: ${Css.pxToRem(16)};
		gap: ${Css.pxToRem(16)};
		width: 100%;
	}
`;

const PoiOpenStatus = styled.div<{ isOpen: boolean }>`
	padding: ${Css.pxToRem(4)} ${Css.pxToRem(10)};
	background-color: ${({ isOpen }) => (isOpen ? Color.Success : Color.Error)};
	color: ${({ isOpen }) => (isOpen ? Color.OnBackground : Color.Neutral1)};
	width: fit-content;
	border-radius: 4px;
`;

const PoiCardTitle = styled.div`
	font-weight: 500;
	color: ${Color.OnBackground};

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		overflow: hidden;
		display: -webkit-box;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
	}
`;

const PoiCardMeta = styled.div`
	display: flex;
	justify-content: space-between;
`;

const PoiBadges = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: ${Css.pxToRem(4)};
`;

const PoiCardBadge = styled.span<{}, ITheme>`
	display: flex;
	align-items: center;
	border: 1px solid ${Color.BorderCard};
	padding: ${Css.pxToRem(4)} ${Css.pxToRem(6)};
	border-radius: ${Css.pxToRem(4)};
	color: ${Color.OnBackground};
	font-size: ${Css.pxToRem(12)};
	height: ${Css.pxToRem(20)};
`;

const PoiImageWrap = styled.div`
	width: 100%;
	height: 100%;

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		max-width: ${POI_MOBILE_IMAGE_WIDTH};
	}
`;

const PoiImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
	transition: ${HOVER_ZOOM_IMAGE_TRANSITION};
`;

const PoiNoImageWrap = styled.div<{}, ITheme>`
	object-fit: cover;
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: ${Color.Neutral1};
	border-radius: 8px;

	svg {
		margin-bottom: ${Css.pxToRem(120)};
		transition: ${HOVER_ZOOM_IMAGE_TRANSITION};

		path {
			fill: ${(props) => props.theme.color.secondary};
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		border-radius: ${Css.pxToRem(8)} 0 0 ${Css.pxToRem(8)};
		max-width: ${POI_MOBILE_IMAGE_WIDTH};

		svg {
			margin-bottom: initial;
			width: ${Css.pxToRem(25)};
			height: ${Css.pxToRem(25)};
		}
	}
`;

const PoiCardIcon = styled.span<{}, ITheme>`
	position: absolute;
	top: ${Css.pxToRem(-24)};
	right: ${Css.pxToRem(8)};
	background: ${Color.Neutral2};
	border-radius: 9999px;
	padding: ${Css.pxToRem(4)};
	width: ${Css.pxToRem(45)};
	height: ${Css.pxToRem(45)};
	display: flex;
	justify-content: center;

	svg {
		width: 100%;
		height: 100%;

		path {
			fill: ${(props) => props.theme.color.secondaryLight};
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		top: ${Css.pxToRem(8)};
	}
`;

const PoiCardIconInner = styled.div`
	border-radius: 50%;
	border: 1px solid ${Color.BorderCard};
	padding: ${Css.pxToRem(6)};
`;

const DetailsLabel = styled.div<{}, ITheme>`
	font-weight: 700;
	font-size: ${Css.pxToRem(16)};
`;

const PoiCardLinkContainer = styled.div<{}, ITheme>`
	display: flex;
	align-items: flex-end;
	gap: ${Css.pxToRem(12)};
	color: ${(props) => props.theme.button.textButton.font.color};

	&:hover {
		color: ${(props) => props.theme.button.textButton.hover.font.color};

		svg {
			path {
				fill: currentColor;
			}
		}
	}
`;
